<template>
  <v-row
    class="my-0 chip-selector"
    :data-qa="`chip-selector-${type}`"
    :class="`chip-selector-${type}`"
  >
    <v-col class="py-1 pr-0">
      <v-row class="my-0">
        <v-col class="py-0">
          <v-autocomplete v-if="useAutocomplete"
            ref="vchipautocomplete"
            v-model="textdata"
            @update:search="searchItems"
            :auto-select-first="true"
            :items="items"
            :label="label != '' ? label : $t('Add')"
            @keyup.enter="setTextItem"
            :hide-details="hint ? false : hideDetails"
            :hint="hint"
		        :persistent-hint="hint ? true : false"
            :item-title="property"
            :error-messages="errors"
            :append-icon="showEnterButton ? 'ecc:ecc-TB-Enter' : undefined"
            @click:append="setTextItem"
          >
            <!-- ICON BEFORE EACH DROPDOWN ITEM -->
            <template #item="{ props, item }">
              <v-list-item v-bind="props">
                <template #prepend>
                  <v-icon v-if="type === 'groups' && item && item.raw?.type">
                    {{ getIconDeviceGroup(item.raw) }}
                  </v-icon>
                </template>
              </v-list-item>
            </template>
            <!-- ICON IN SELECTED VALUE (INPUT FIELD) -->
            <template #selection="{ item }">
              <div class="d-flex align-center ga-2">
                <v-icon
                  v-if="type === 'groups' && item.raw?.type"
                  :icon="getIconDeviceGroup(item.raw)"
                  size="small"
                />
                <span>{{ item.title }}</span>
              </div>
            </template>
          </v-autocomplete>
          <v-text-field v-else
            ref="textfield"
            v-model="textdata"
            :label="label != '' ? label : $t('Add')"
            @keypress="handleInputEvent"
            @keyup.enter="setTextItem"
            :append-icon="showEnterButton ? 'ecc:ecc-TB-Enter' : undefined"
            @click:append="setTextItem"
            :rules="rules"
          />
        </v-col>
      </v-row>
      <v-row
        v-if="choosenItems.length"
        class="my-1 mx-2"
        :data-qa="`chip-list-${type}`"
      >
        <v-chip
          class="ecc max-100 mr-1 mb-1"
          v-for="item in choosenItems"
          :key="item.id"
          closable
          @click:close="removeItem(item)"
        >
          <v-icon v-if="type === 'groups' && item && item.type" left class="mr-1">
            {{ getIconDeviceGroup(item) }}
          </v-icon>
          {{ item[property] }}
        </v-chip>
      </v-row>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { dataStore } from "../../stores/data";
import { DeviceGroupType } from "../../utils/type.util";

export default defineComponent({
  name: "VChipComponent",
  data() {
    return {
      dataStore: dataStore(),
      textdata: "",
      errors: [],
    };
  },
  props: {
    hint: {
			type: String,
			default: ''
		},
		hideDetails: {
			type: Boolean,
			default: true,
		},
    autocomplete: {
      type: Boolean,
      default: () => false,
    },
    showEnterButton: {
      type: Boolean,
      default: () => false,
    },
    items: {
      type: Array,
      default: () => [],
    },
    choosenItems: {
      type: Array,
      default: () => [],
    },
    property: {
      type: String,
      default: "name",
    },
    additionalChipInfo: {
      type: Object,
      default: () => {},
    },
    type: {
      type: String,
    },
    readonly: {
      type: Boolean,
      default: () => false,
    },
    label: {
      type: String,
      default: () => "",
    },
    maxChips: {
      type: Number,
      default: Number.POSITIVE_INFINITY,
    },
    disabled: {
      type: Boolean,
      default: () => false,
    },
    rules: {
      type: Array as PropType<((value: any) => boolean | string)[]>,
      default: () => [],
    },
  },
  methods: {
    searchItems(value) {
      this.$emit("searchItems", value);
    },

    setTextItem(event) {
      if (this.rules) {
        for (let rule of this.rules) {
          const result = rule(this.textdata);
          if (result !== true) {
            return;
          }
        }
      }

      const item = this.useAutocomplete 
        ? this.items.find((i: any) => i[this.property] === this.textdata) // return original element from items
        : { [this.property]: this.textdata }; // create new object with only property
      this.setItem(item);
      this.textdata = "";
      this.$refs['vchipautocomplete']?.$el.querySelector('input').blur()
      this.errors = [];
    },

    setItem(value) {
      if (!value || this.readonly || !this.canAddChips) {
        if (!value) {
          this.errors = [this.$t('selectValidItem') as string];
        }
        return;
      }
      
      this.$emit("setItem", value);
    },

    handleInputEvent() {
      this.$nextTick(() => {
        if (this.$refs.autocomplete)
          (this.$refs.autocomplete as any).select = null;
      });
    },

    removeItem(item) {
      this.$emit("removeItem", item);
    },

    roleGroups(role) {
      return this.additionalChipInfo && this.additionalChipInfo[role.id]
        ? this.additionalChipInfo[role.id].groups
        : [];
    },
    getTextWidth(canvas, text, font) {
      const context = canvas.getContext("2d");
      context.font = font;
      const metrics = context.measureText(text);
      return metrics.width;
    },
    getIconDeviceGroup(item): string {
      switch (item.type) {
        case DeviceGroupType.SELF_DEVICE:
        return "ecc:ecc-A-Device";
        case DeviceGroupType.ALL_DEVICES:
        case DeviceGroupType.STANDARD:
        return "ecc:ecc-T-DeviceGroupList";
      }
      return "";
    }
  },
  computed: {
    canAddChips(): boolean {
      return this.choosenItems.length < this.maxChips && !this.disabled;
    },
    useAutocomplete(): boolean {
      return this.autocomplete || this.items?.length > 0;
    },
  },
  watch: {
    "dataStore.getShowEditPanel"(val): void {
      if (val === false) {
        this.textdata = "";
        this.errors = [];
        this.$refs['vchipautocomplete']?.$el.querySelector('input').blur()
      }
    },
  },
});
</script>

<style scoped>
.ellipsis-text {
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
}
.max-100 {
  max-width: 100%;
}
.hint  {
  font-size: 13px;
  color: var(--color-g4);
}
>>> .v-tooltip__content {
  pointer-events: auto;
}
.chip-dense-container {
  max-height: 32px;
  overflow: hidden;
}
.hidden-chips-counter {
  position: absolute;
  right: 0px;
  height: 32px;
  line-height: 32px;
}
.chip-g {
  width: 100%;
}
.chip-menu {
  padding: 10px;
  background: var(--color-wt);
  width: 290px;
  max-width: 290px;
}
.chips-container {
  max-width: 100%;
}

.ecc.v-chip .close {
  color: var(--color-dk) !important;
}

</style>
